import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import type { BulkPublishOutcome, Product, PublishState, Variant } from '../api/types'
import { Banner, Button, ErrorBanner, Field, Panel, Spinner, TextArea, TextInput } from '@shop/ui'

/**
 * نشرُ الصنف على `aqabamobile.com` — <b>لوحةٌ مستقلّةٌ عن تحرير الصنف</b>.
 *
 * والفصلُ مقصودٌ في الشاشة كما هو في الصلاحيّة: تعديلُ اسمٍ خطأٌ يُصحَّح في
 * الداخل ولا يراه أحد، <b>والنشرُ يُخرج الصنفَ إلى العالم</b>. ولوحةٌ واحدةٌ
 * تخلط الاثنين تجعل «حفظ» فعلًا لا يعرف صاحبُه مداه.
 *
 * ⚠ <b>ولا تظهر هذه اللوحةُ لمن لا يملك <c>ProductPublish</c></b> — ولا يكفي
 *   إخفاؤها: النقطةُ في الخادم تحرسها الصلاحيّةُ نفسُها، وهذا إخفاءُ ما لا
 *   يستطيع لا حراسةٌ.
 */
export default function PublishingPanel({ product, onDone }: { product: Product; onDone: () => void }) {
  const { can } = useAuth()

  const state = useQuery({
    queryKey: ['publishing', product.id],
    queryFn: () => api<PublishState>(`/api/v1/products/${product.id}/publishing`),
    enabled: can(Perm.productView),
  })

  if (!can(Perm.productPublish)) {
    return null
  }

  if (state.isPending) {
    return (
      <Panel title="النشرُ على الموقع">
        <Spinner />
      </Panel>
    )
  }

  if (state.isError) {
    return (
      <Panel title="النشرُ على الموقع">
        <ErrorBanner error={state.error} />
      </Panel>
    )
  }

  return <PublishingForm product={product} state={state.data} onDone={onDone} />
}

function PublishingForm({
  product,
  state,
  onDone,
}: {
  product: Product
  state: PublishState
  onDone: () => void
}) {
  const [publicName, setPublicName] = useState(state.publicNameAr ?? '')
  const [publicDescription, setPublicDescription] = useState(state.publicDescription ?? '')

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<PublishState>(`/api/v1/products/${product.id}/publishing`, { method: 'PUT', body }),
    onSuccess: onDone,
  })

  /** نشرٌ يشمل القياساتِ المُسعَّرة — ويُعيد ما ظهر منها فعلًا. */
  const publishWithVariants = useMutation({
    mutationFn: async (text: Record<string, unknown>) => {
      // النصُّ العامُّ أوّلًا، ثمّ النشرُ بالجملة.
      await api<PublishState>(`/api/v1/products/${product.id}/publishing`, {
        method: 'PUT',
        body: { ...text, showOnWebsite: false },
      })

      return api<BulkPublishOutcome[]>('/api/v1/products/publishing', {
        method: 'POST',
        body: { productIds: [product.id], showOnWebsite: true },
      })
    },
    onSuccess: (result) => {
      setShown(result[0] ?? null)
      onDone()
    },
  })

  const [shown, setShown] = useState<BulkPublishOutcome | null>(null)

  /**
   * ⚠ <b>و«انشر» تعني ما يفهمه الإنسان: أخرِجه إلى الموقع.</b>
   *
   * <p>
   * كانت تضبط عَلَمَ المنتج وحدَه، والقياساتُ تبقى مُطفَأة — فيخرج الصنفُ
   * «منشورًا» في القاعدة وغائبًا عن الموقع، أو أسوأ: بصفحةٍ فيها
   * <c>0.000 JOD</c> و«غيرُ متوفّر». ووقع فعلًا ٢٠٢٦-٠٩-١٩.
   * </p>
   *
   * <p>
   * والعَلَمان اثنان في القاعدة لسببٍ وجيه — يُخفى قياسٌ ويبقى غيرُه — لكنّ
   * <b>فعلَ المستخدم واحد</b>. فالنشرُ يمرّ بنقطة الجملة: تُنشر معه قياساتُه
   * <b>المُسعَّرة</b> وحدَها، ويُقال إن لم يكن له سعرٌ فلن يظهر.
   * </p>
   */
  const toggle = (showOnWebsite: boolean) => {
    if (!showOnWebsite) {
      // وإلغاءُ النشر يُطفئ القياساتِ في الخادم أصلًا.
      save.mutate({
        showOnWebsite,
        publicNameAr: publicName.trim() || null,
        publicDescription: publicDescription.trim() || null,
      })

      return
    }

    // ⚠ والنصُّ يُرسَل أوّلًا: من كتب اسمًا عامًّا ثمّ ضغط «انشر» يقصد نشرَه
    //   بما كتب — لا أن يضيع ما كتبه لأنّه لم يضغط «احفظ».
    publishWithVariants.mutate({
      publicNameAr: publicName.trim() || null,
      publicDescription: publicDescription.trim() || null,
    })
  }

  const published = state.showOnWebsite
  const busy = save.isPending || publishWithVariants.isPending

  return (
    <Panel
      title="النشرُ على الموقع"
      actions={
        <Button
          tone={published ? 'ghost' : 'primary'}
          disabled={busy || (!published && !product.isActive)}
          onClick={() => toggle(!published)}
        >
          {busy ? 'يُحفَظ…' : published ? 'ألغِ النشر' : 'انشر على الموقع'}
        </Button>
      }
    >
      <div className="space-y-3">
        <Banner tone={published ? 'ok' : 'info'}>
          {published
            ? `معروضٌ للزبائن على aqabamobile.com — ${state.publishedVariants} من ${state.totalVariants} متغيّرًا.`
            : 'غيرُ معروضٍ للعامّة. لا يراه أحدٌ خارج النظام.'}
        </Banner>

        {/* ⚠ والرابطُ يُعرض لأنّه **لا يتغيّر بعد أوّل نشر**: من يشاركه في
            واتساب يجب أن يعرف أنّه ثابت، ومن يريد تغييرَه يعرف أنّه لا يستطيع. */}
        {state.slug && (
          <Row label="الرابطُ العامّ">
            <span dir="ltr" className="tabular">/p/{state.slug}</span>
          </Row>
        )}

        {published && state.totalVariants > 0 && state.publishedVariants === 0 && (
          <Banner tone="warn">
            لا متغيّرَ معروضًا — فالصفحةُ تظهر بلا سعرٍ ولا توفّر. اعرض متغيّرًا
            واحدًا على الأقلّ من قائمة المتغيّرات.
          </Banner>
        )}

        {!product.isActive && !published && (
          <Banner tone="warn">لا يُنشر صنفٌ مؤرشَف. أعِد تفعيلَه أوّلًا.</Banner>
        )}

        <Field
          label="الاسمُ للعامّة"
          hint="اتركه فارغًا ليُعرض الاسمُ الداخليّ. وتعديلُ الداخليّ لا يمسّ هذا."
        >
          <TextInput value={publicName} onChange={setPublicName} disabled={save.isPending} />
        </Field>

        <Field label="الوصفُ للعامّة" hint="ما يقرؤه الزبون. اتركه فارغًا ليُعرض الوصفُ الداخليّ.">
          <TextArea
            value={publicDescription}
            onChange={setPublicDescription}
            rows={3}
            disabled={save.isPending}
          />
        </Field>

        <div className="flex flex-wrap gap-2">
          <Button
            disabled={save.isPending}
            onClick={() =>
              save.mutate({
                publicNameAr: publicName.trim() || null,
                publicDescription: publicDescription.trim() || null,
              })
            }
          >
            احفظ النصَّ العامّ
          </Button>

          {(state.publicNameAr || state.publicDescription) && (
            <Button
              disabled={save.isPending}
              onClick={() => {
                setPublicName('')
                setPublicDescription('')
                // ⚠ ومحوٌ صريحٌ لا إرسالُ فراغ: `null` تعني «اتركه» في الخادم،
                //   والفرقُ بين «اتركه» و«امحُه» قرارٌ يُكتب لا يُخمَّن.
                save.mutate({ clearPublicText: true })
              }}
            >
              امحُ النصَّ العامّ
            </Button>
          )}
        </div>

        <ErrorBanner error={save.error ?? publishWithVariants.error} />

        {/* ⚠ **وما ظهر فعلًا يُقال** — لا «تمّ» تترك صاحبَها يفتح الموقعَ ليرى. */}
        {shown && (
          <Banner tone={shown.shownVariants > 0 ? 'ok' : 'warn'}>
            {shown.shownVariants > 0
              ? `ظهر على الموقع بـ${shown.shownVariants} قياسًا مُسعَّرًا.`
              : shown.note ?? 'نُشر — ولم يظهر قياسٌ واحد.'}
          </Banner>
        )}

        {/* ⚠ وتفعيلُ المتغيّرات هنا لا في قائمة المتغيّرات: كلُّ ما يخصّ
            «ما يراه الزبون» في لوحةٍ واحدة. وموزّعًا على شاشتين يصير النشرُ
            فعلًا لا يعرف صاحبُه أين يكمله. */}
        {published && product.variants.length > 0 && (
          <div className="space-y-1 border-t border-line pt-3">
            <p className="text-xs text-ink-soft">ما يُعرض من المتغيّرات:</p>

            {product.variants
              .filter((variant) => variant.isActive)
              .map((variant) => (
                <VariantToggle key={variant.id} variant={variant} onDone={onDone} />
              ))}
          </div>
        )}
      </div>
    </Panel>
  )
}

function VariantToggle({ variant, onDone }: { variant: Variant; onDone: () => void }) {
  const save = useMutation({
    mutationFn: (showOnWebsite: boolean) =>
      api<PublishState>(`/api/v1/variants/${variant.id}/publishing`, {
        method: 'PUT',
        body: { showOnWebsite },
      }),
    onSuccess: onDone,
  })

  const label = [variant.colour, variant.capacity].filter(Boolean).join(' · ') || variant.sku

  return (
    <div className="flex items-center justify-between gap-3 py-1 text-sm">
      <span>{label}</span>

      <Button
        tone={variant.showOnWebsite ? 'ghost' : 'primary'}
        disabled={save.isPending}
        onClick={() => save.mutate(!variant.showOnWebsite)}
      >
        {variant.showOnWebsite ? 'أخفِه' : 'اعرضه'}
      </Button>
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-ink-soft">{label}</span>
      <span className="font-semibold">{children}</span>
    </div>
  )
}
